import { ECardinality, ESymbolCategory } from "@kuika/kuika-cl-model";
import type { CascaderOptionType } from "antd/es/cascader";
import type { ISymbolPickerActionResultOrigin } from "../../../../../shared/utility/symbol-picker-action-order";
import {
  displaySymbolPickerTypeName,
  getSymbolPickerTypeCompatibility,
  normalizeSymbolPickerTypeName,
  type ISymbolPickerTypeCompatibility,
  type ISymbolPickerValueShape
} from "../../../../../shared/utility/symbol-picker-type-compat";
import {
  getSymbolPickerCategoryDoc,
  getSymbolPickerConversionDoc,
  getSymbolPickerDefaultValueDoc,
  type ISymbolPickerCategoryDoc,
  type ISymbolPickerConversionDoc
} from "../../../../../shared/utility/symbol-picker-value-docs";
import {
  ESymbolPickerNodeKind,
  getSymbolPickerNodeChain,
  isSymbolPickerPickable,
  readSymbolPickerTypeName,
  type ISymbolPickerNode
} from "./symbol-picker-node";

/**
 * What the preview pane knows beyond the option tree — facts about the HOST (the field being bound)
 * and lookups into the screen, handed down by SymbolPickerCascader. Every member is optional: a
 * block whose fact is missing is not drawn, and a popup given no context draws exactly what it drew
 * before (the golden test's popup is given none).
 */

/** The field the value is being bound to. */
export interface ISymbolPickerBindingTarget {
  /** The parameter's display name (else its name), or the component property's name. */
  name: string;
  /** "String", "List of Order". */
  typeLabel?: string;
  /** The parameter's doc line (action docs), or the property definition's description. */
  description?: string;
  /** For a component property: the component's name. */
  componentName?: string;
  /** For an action parameter: the action's label. */
  actionName?: string;
}

/** A component the pointed value reads from. */
export interface ISymbolPickerComponentFacts {
  /** The component's type ("Label", "TextInput"). */
  typeName?: string;
  /** The property's current design-time text, when it is a plain value (not bound to anything). */
  designValue?: string;
  /** The nearest list component (Table, Gallery, Repeater…) the component repeats inside. */
  repeatingParentName?: string;
}

export interface ISymbolPickerPreviewContext {
  target?: ISymbolPickerBindingTarget;
  /** The host's type and cardinality, for the compatibility badge. No type → no badge. */
  targetShape?: ISymbolPickerValueShape;
  /** Where an action result comes from and whether it has run yet at the edited step. */
  describeActionResult?: (resultActionId: string) => ISymbolPickerActionResultOrigin | undefined;
  /** Facts about the component behind a Components option (a component row or one of its properties). */
  describeComponent?: (option: CascaderOptionType) => ISymbolPickerComponentFacts | undefined;
}

/** Category rows below the root that have their own description — the groups inside these roots. */
const GROUPING_ROOTS: ReadonlySet<string> = new Set<string>(["Default", "Process Parameters"]);

const LIST_CAPTION_PREFIX: string = "List of ";

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

function readStringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry: unknown): entry is string => typeof entry === "string" && entry !== "")
    : [];
}

function readOwnTypeName(option: CascaderOptionType): string | undefined {
  return (
    readSymbolPickerTypeName(option.toType) ??
    readSymbolPickerTypeName(option.currentType) ??
    readSymbolPickerTypeName(option.domainType) ??
    readSymbolPickerTypeName(option.fieldType) ??
    readSymbolPickerTypeName(option.fieldtype)
  );
}

/**
 * The type a type conversion produces: the host's type when the conversion lists it (the engine
 * offers a conversion because it produces the host's type), else the first type it lists.
 */
function getConversionResultType(
  option: CascaderOptionType,
  target: ISymbolPickerValueShape | undefined
): string | undefined {
  const toTypes: string[] = readStringList(option.toTypes);
  const wanted: string | undefined = normalizeSymbolPickerTypeName(target?.typeName);
  if (wanted && toTypes.includes(wanted) && target?.typeName) {
    return displaySymbolPickerTypeName(target.typeName);
  }
  return toTypes[0];
}

/**
 * The value's type and cardinality, read off the option (and, for a conversion, off what it
 * converts). Undefined members when the option does not say.
 */
export function resolveSymbolPickerValueShape(
  node: ISymbolPickerNode,
  target?: ISymbolPickerValueShape
): ISymbolPickerValueShape {
  const option: CascaderOptionType = node.option;
  if (option.category === ESymbolCategory.typeConvertion) {
    return { typeName: getConversionResultType(option, target), cardinality: ECardinality.One };
  }
  if (option.category === ESymbolCategory.cardinalityConvertion) {
    const parentShape: ISymbolPickerValueShape = node.parent ? resolveSymbolPickerValueShape(node.parent, target) : {};
    return { typeName: parentShape.typeName, cardinality: ECardinality.One };
  }
  const caption: string | undefined = node.typeCaption;
  const isListCaption: boolean = caption?.startsWith(LIST_CAPTION_PREFIX) === true;
  const typeName: string | undefined =
    readOwnTypeName(option) ?? (isListCaption && caption ? caption.slice(LIST_CAPTION_PREFIX.length) : caption);
  let cardinality: ECardinality | undefined;
  if (option.cardinality === ECardinality.One || option.cardinality === ECardinality.Many) {
    cardinality = option.cardinality;
  } else if (isListCaption) {
    cardinality = ECardinality.Many;
  }
  return { typeName, cardinality };
}

/** The badge for a pickable leaf, or undefined (no host type, an open host type, an untyped value). */
export function getSymbolPickerNodeCompatibility(
  node: ISymbolPickerNode,
  target: ISymbolPickerValueShape | undefined
): ISymbolPickerTypeCompatibility | undefined {
  if (!target || !isSymbolPickerPickable(node)) {
    return undefined;
  }
  return getSymbolPickerTypeCompatibility(resolveSymbolPickerValueShape(node, target), target);
}

/**
 * A category row's description: anything in the root column (a category, or a root-level value such
 * as Fixed Value), or a group inside Default / Process Parameters.
 */
export function getSymbolPickerNodeCategoryDoc(node: ISymbolPickerNode): ISymbolPickerCategoryDoc | undefined {
  if (node.kind === ESymbolPickerNodeKind.loading) {
    return undefined;
  }
  const isRoot: boolean = node.depth === 0;
  const isGroup: boolean =
    node.kind === ESymbolPickerNodeKind.branch &&
    node.depth === 1 &&
    node.parent !== null &&
    GROUPING_ROOTS.has(node.parent.name.trim());
  if (!isRoot && !isGroup) {
    return undefined;
  }
  return getSymbolPickerCategoryDoc(node.name);
}

/** A built-in Default value's description. */
export function getSymbolPickerNodeDefaultDoc(node: ISymbolPickerNode): string | undefined {
  const option: CascaderOptionType = node.option;
  if (node.kind !== ESymbolPickerNodeKind.leaf || option.category !== ESymbolCategory.default) {
    return undefined;
  }
  if (option.defaultValueType === undefined || option.defaultValueType === null) {
    return undefined;
  }
  return getSymbolPickerDefaultValueDoc(node.name);
}

export interface ISymbolPickerConversionPreview {
  doc?: ISymbolPickerConversionDoc;
  /** The type the conversion produces, when the option says. */
  resultType?: string;
}

/** A type or list conversion's description, example and resulting type. */
export function getSymbolPickerNodeConversion(
  node: ISymbolPickerNode,
  target: ISymbolPickerValueShape | undefined
): ISymbolPickerConversionPreview | undefined {
  const option: CascaderOptionType = node.option;
  if (option.category === ESymbolCategory.typeConvertion) {
    const key: string | undefined = readString(option.typeConversion);
    return {
      doc: key ? getSymbolPickerConversionDoc(key) : undefined,
      resultType: getConversionResultType(option, target)
    };
  }
  if (option.category === ESymbolCategory.cardinalityConvertion) {
    return {
      doc: getSymbolPickerConversionDoc(node.name),
      resultType: resolveSymbolPickerValueShape(node, target).typeName
    };
  }
  return undefined;
}

/** The action result the node reads from (itself, or its nearest action-result ancestor). */
export function findSymbolPickerActionResultId(node: ISymbolPickerNode): string | undefined {
  const chain: ISymbolPickerNode[] = getSymbolPickerNodeChain(node);
  for (let index = chain.length - 1; index >= 0; index--) {
    const option: CascaderOptionType = chain[index].option;
    if (option.category === ESymbolCategory.actionResult) {
      return readString(option.resultActionId);
    }
  }
  return undefined;
}

/** The Components option the node reads from: a component row or one of its properties. */
export function findSymbolPickerComponentOption(node: ISymbolPickerNode): CascaderOptionType | undefined {
  const chain: ISymbolPickerNode[] = getSymbolPickerNodeChain(node);
  if (chain[0]?.option.category !== ESymbolCategory.components) {
    return undefined;
  }
  for (let index = chain.length - 1; index >= 0; index--) {
    const option: CascaderOptionType = chain[index].option;
    if (option.category === ESymbolCategory.displayItemProp || option.category === ESymbolCategory.displayItem) {
      return option;
    }
  }
  return undefined;
}
